import React, { useRef, useEffect, useContext } from 'react';
import { Content, LayoutContext } from '@layout';
import styles from './Hero.module.scss';
import usePageVisible from '@hooks/usePageVisible';
import videoSrc from './video.mp4';
import { Button, ButtonsWrapper } from '@input';
import { useInView } from 'react-intersection-observer';

export default React.memo(function Hero() {
  const [ref, inView] = useInView({
    threshold: 0.1
  });

  const initiallyInView = useRef(inView);
  const video = useRef(null);
  const [pageVisible, pageFocused] = usePageVisible();
  const pauseTimer = useRef(null);
  const { mobileMenuOpen } = useContext(LayoutContext);

  useEffect(() => {
    const handler = () => {
      video.current.muted = true;
      if (initiallyInView.current) {
        video.current.play();
      }

      document.removeEventListener('mousemove', handler);
      document.removeEventListener('touchstart', handler);
    };

    document.addEventListener('mousemove', handler);
    document.addEventListener('touchstart', handler);

    return () => {
      document.removeEventListener('mousemove', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, []);

  useEffect(() => {
    clearTimeout(pauseTimer.current);

    if (!inView || !pageVisible) {
      video.current?.pause();
    } else if (pageVisible && pageFocused && inView && !mobileMenuOpen) {
      video.current?.play();
    } else if (!pageFocused || !inView || mobileMenuOpen) {
      const delay = mobileMenuOpen ? 400 : 8000;

      pauseTimer.current = setTimeout(() => {
        video.current?.pause();
      }, delay);
    }
  }, [pageVisible, pageFocused, inView, mobileMenuOpen]);

  return (
    <section
      className={styles.wrapper}
      ref={ref}
    >
      <Content>
        <h1>
          Scientific innovation
          <br /> and collaboration
        </h1>
        <p>
          We are a diverse team of scientists studying the chemistry of
          natural compounds and models to understand how the Earth works.
        </p>
        <ButtonsWrapper className={styles.cta}>
          <Button
            theme="white"
            icon="atom"
          >
            About the lab
          </Button>

          <Button
            theme="white"
            icon="scholar"
            to="/"
          >
            Meet the Team
          </Button>
        </ButtonsWrapper>
      </Content>
      <video
        ref={video}
        src={videoSrc}
        className={styles.video}
        preload="metadata"
        controls={false}
        muted
        playsInline
        autoPlay
        loop
      />
    </section>
  );
});
